import { LinkOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useMemo, ReactNode } from 'react';
import { Divider, Space, Tag } from 'antd';
import { useTranslate } from '@tolgee/react';
import { ItemLink } from 'views/Conversation/Sider/TradeProduct';
import SafeImage from 'components/SafeImage';
import Copy from 'views/Conversation/Sider/Copy';
import EllipsisText from 'components/EllipsisText';
import { useMemoizedFn } from 'ahooks';
import toLowerCase from 'utils/toLowerCase';
import { openExternalURL } from 'utils/openExternalURL';
import { getImageThumb } from 'lib/utils/platformImage';
import { useXiapibuyEnable } from 'hooks/useXiapibuyEnable';

export interface ItemCardData {
  id: string | number;
  title: string;
  price?: number | string | false;
  originPrice?: ReactNode;
  discount?: ReactNode;
  img: string;
  link: string;
  skuName?: string;
  sellerSku?: string;
  itemSku?: string;
  isPreOrder?: boolean;
  externalStoreId: string;
  spuStockStatus: 'InStock' | 'OutOfStock' | 'PartialOutOfStock';
}
export interface ItemCardProps {
  priceFormatter?: (price: number | string) => string;
  footer?: React.ReactNode;
  dataSource: ItemCardData;
  infoAfter?: React.ReactNode;
  platform?: General.PlatformUpperCaseTunnel;
  region?: General.UpperCaseRegion;
  lazyScrollContainer?: Element | null;
}

const Card = styled.div`
  56px: 56px;
  display: flex;
  position: relative;
  margin: 10px 0;
  color: #515359;
  cursor: pointer;
  &:first-of-type {
    margin: 0;
  }
  overflow: hidden;
  > section {
    display: none;
  }
  &:hover {
    > section {
      display: block;
    }
  }
`;

export const ItemImg = styled(SafeImage)`
  width: 56px;
  height: 56px;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: ${({ src }) => `url(${src})`};
  flex: 1 1 56px;
`;
const Info = styled.div`
  max-width: calc(100% - 56px);
  margin-left: 4px;
  padding: 0 4px;
  display: flex;
  color: #95979a;
  flex-direction: column;
  // justify-content: space-between;
  flex: 30;

  &:hover {
    background: #f7f9fa;
  }
`;
const TitleWrap = styled.div`
  display: flex;
  margin-bottom: 4px;

  .ant-tag {
    border: none;
    color: #00bf69;
    margin-right: 2px;
    padding: 0 4px;
    font-size: 12px;
    flex-shrink: 0;
    align-items: center;
  }
`;
export const ItemTitle = styled.div`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 18px;
  color: #515359;
`;
const Sku = styled.div`
  font-size: 12px;
  color: #84868c;
  display: flex;
  align-items: center;
  span:nth-child(1) {
    margin-right: 8px;
    flex-shrink: 0;
  }
  span:nth-child(2) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 4px;
  }
`;

const Extra = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #999;
`;

export const Price = styled.div`
  font-size: 12px;
  color: #84868c;
`;

const ItemCard = (props: ItemCardProps) => {
  const { t } = useTranslate();

  const {
    footer: footerProp,
    dataSource: {
      id,
      title,
      price = false,
      originPrice,
      discount,
      img,
      link,
      skuName,
      sellerSku,
      itemSku,
      isPreOrder,
      externalStoreId,
    },
    infoAfter,
    priceFormatter = (price) => (typeof price === 'number' ? `${price.toFixed(2)}` : price),
    platform,
    region,
    lazyScrollContainer,
  } = props;

  const xiapibuyEnable = useXiapibuyEnable(region);

  let url = link;
  if (platform === 'SHOPEE') {
    if (xiapibuyEnable && region && url && !url.includes('.xiapibuy.com')) {
      url = `https://${toLowerCase(region)}.xiapibuy.com/product/${externalStoreId}/${id}`;
    }
  }

  const stopPropagation = useMemoizedFn((e) => e.stopPropagation());

  const footer = useMemo(() => {
    if (footerProp || price === false) {
      return footerProp;
    }

    const priceElement = <Price>{priceFormatter(price)}</Price>;

    const extra =
      originPrice && discount ? (
        <Extra>
          <del>{originPrice}</del>
          {discount}
        </Extra>
      ) : null;

    return (
      <>
        {priceElement}
        {extra}
      </>
    );
  }, [footerProp, priceFormatter, price, originPrice, discount]);

  const ItemSkuUI = itemSku ? (
    <Sku>
      <span>{t('sider.主商品货号')}:</span>
      <EllipsisText text={itemSku} maxWidth={100} />
      <Copy
        text={itemSku}
        successMessage={t('trade.copySucceed')}
        failedMessage={t('trade.copyFailed')}
        onClick={stopPropagation}
      />
    </Sku>
  ) : null;

  const handleClick = useMemoizedFn((e) => {
    openExternalURL(url, '__blank');
  });

  return (
    <Card key={id} onClick={handleClick}>
      <ItemImg hasSmallPic="both" src={getImageThumb(img, platform)} target={lazyScrollContainer} />
      <Info>
        <TitleWrap onClick={stopPropagation}>
          {isPreOrder ? (
            <Tag color="green" title={t('trade.预购商品')}>
              {t('trade.预购商品')}
            </Tag>
          ) : null}
          <ItemTitle>{title}</ItemTitle>
        </TitleWrap>
        <Space size={0} wrap split={<Divider type="vertical" />}>
          {footer}
          {skuName ? (
            <Sku>
              <span>SKU:</span>
              <EllipsisText text={skuName} maxWidth={80} />
              <Copy
                text={skuName}
                successMessage={t('trade.copySucceed')}
                failedMessage={t('trade.copyFailed')}
                onClick={(e) => e.stopPropagation()}
              />
            </Sku>
          ) : null}
          {sellerSku ? (
            <Sku>
              <span>{t('sider.SKU货号')}:</span>
              <EllipsisText text={sellerSku} maxWidth={80} />
              <Copy
                text={sellerSku}
                successMessage={t('trade.copySucceed')}
                failedMessage={t('trade.copyFailed')}
                onClick={(e) => e.stopPropagation()}
              />
            </Sku>
          ) : (
            ItemSkuUI
          )}
          {infoAfter}
        </Space>
      </Info>
      <ItemLink>
        <LinkOutlined />
      </ItemLink>
    </Card>
  );
};
export default ItemCard;
